import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { InfoButton, type InfoTopic } from "@/components/info-button";

/**
 * "Home or business?" on a city fee line (migration 717, Corbin 2026-10-28).
 *
 * Some cities charge a home a smaller city fee than a business. The app picks
 * the fee from the customer's type (Residential / Commercial on the customer
 * page). When the type isn't set it bills the business fee and this strip asks
 * — amber, so the office sees it. One tap swaps the line to the other fee.
 * On a line whose customer IS marked, the pick still shows, quietly, for the
 * odd job that's the other kind (a business owner's own house).
 */
export type FeeAudience = "residential" | "commercial";

export interface FeeAudienceInfo {
    current: FeeAudience;
    picked: boolean;
    customer_known: boolean;
    other_price: number | null;
}

export interface FeeAudienceLine {
    id: string;
    fee_audience?: FeeAudienceInfo | null;
}

export const FEE_AUDIENCE_CHOICES: Array<{ value: FeeAudience; label: string }> = [
    { value: "residential", label: "Home" },
    { value: "commercial", label: "Business" },
];

export const FEE_AUDIENCE_HELP: InfoTopic = {
    title: "Home or business?",
    lines: [
        "This city charges a home one city fee and a business another.",
        "We use the customer's type: Residential or Commercial, on the customer page.",
        "No type set? We charge the business fee and ask here.",
        "Tap Home or Business. The price changes to match.",
    ],
};

/** True when the strip should ask (amber): type unknown and nobody picked yet. */
export function needsFeeAudiencePick(l: FeeAudienceLine): boolean {
    const a = l.fee_audience;
    return !!a && !a.customer_known && !a.picked;
}

/** Hook-free gate: most lines are not split city fees. */
export function FeeAudiencePick({
    line,
    doc,
    docId,
    canEdit,
}: {
    line: FeeAudienceLine;
    doc: "estimate" | "invoice";
    docId: string | null | undefined;
    canEdit: boolean;
}) {
    if (!line.fee_audience || !docId) return null;
    return <Picker line={line} info={line.fee_audience} doc={doc} docId={docId} canEdit={canEdit} />;
}

function Picker({
    line,
    info,
    doc,
    docId,
    canEdit,
}: {
    line: FeeAudienceLine;
    info: FeeAudienceInfo;
    doc: "estimate" | "invoice";
    docId: string;
    canEdit: boolean;
}) {
    const qc = useQueryClient();
    const ask = needsFeeAudiencePick(line);
    const pick = useMutation({
        mutationFn: (audience: FeeAudience) =>
            api.post(`/api/${doc === "estimate" ? "estimates" : "invoices"}/${docId}/lines/${line.id}/fee-audience`, {
                audience,
            }),
        onSuccess: async (_r, audience) => {
            await qc.invalidateQueries({ queryKey: [doc, docId] });
            toast.success(audience === "residential" ? "City fee set: home" : "City fee set: business");
        },
        onError: (e: any) =>
            toast.error(`NOT changed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    return (
        <span
            className={cn(
                "flex flex-wrap items-center gap-2 rounded-md px-2 py-1 text-[12px]",
                ask ? "border border-amber-300 bg-amber-50 text-amber-900" : "text-foreground/70",
            )}
            data-testid="fee-audience-pick"
        >
            {ask && <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            <span className={cn(ask && "font-semibold")}>{ask ? "Home or business?" : "City fee for:"}</span>
            {FEE_AUDIENCE_CHOICES.map((c) => {
                const active = info.current === c.value;
                return (
                    <button
                        key={c.value}
                        type="button"
                        aria-pressed={active}
                        disabled={!canEdit || pick.isPending || active}
                        onClick={() => pick.mutate(c.value)}
                        className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-semibold disabled:cursor-default",
                            active
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-card text-foreground hover:bg-accent",
                        )}
                    >
                        {c.label}
                        {!active && info.other_price != null && (
                            <span className="text-[10.5px] font-normal opacity-80">${info.other_price.toFixed(2)}</span>
                        )}
                    </button>
                );
            })}
            {pick.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-label="Saving" />}
            <InfoButton topic={FEE_AUDIENCE_HELP} className="h-6 w-6 text-xs" />
        </span>
    );
}
